import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FaIcons from 'react-icons/fa';

const I = (Icon: any) => Icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaCalendarAlt = I(FaIcons.FaCalendarAlt);
const FaClock = I(FaIcons.FaClock);
const FaMapMarkerAlt = I(FaIcons.FaMapMarkerAlt);
const FaPhone = I(FaIcons.FaPhone);
const FaRobot = I(FaIcons.FaRobot);
const FaPlane = I(FaIcons.FaPlane);
const FaGamepad = I(FaIcons.FaGamepad);
const FaBookOpen = I(FaIcons.FaBookOpen);
const FaTrophy = I(FaIcons.FaTrophy);
const FaBrain = I(FaIcons.FaBrain);
const FaMusic = I(FaIcons.FaMusic);
const FaArrowRight = I(FaIcons.FaArrowRight);
const FaChild = I(FaIcons.FaChild);
const FaUserGraduate = I(FaIcons.FaUserGraduate);
const FaUserTie = I(FaIcons.FaUserTie);
const FaCheckCircle = I(FaIcons.FaCheckCircle);
const FaBolt = I(FaIcons.FaBolt);
const FaCertificate = I(FaIcons.FaCertificate);
const FaTools = I(FaIcons.FaTools);
const FaUsers = I(FaIcons.FaUsers);

const navy = '#0a1f44';
const navyMid = '#0f2c5e';
const yellow = '#fbbf24';
const yellowDark = '#f59e0b';
const ink = '#0f172a';
const muted = '#64748b';
const line = '#e2e8f0';
const cream = '#fffaf0';

// ---------- Section helper ----------
const Reveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] } }
      }}
    >
      {children}
    </motion.div>
  );
};

// ---------- Layout ----------
const Page = styled.div`
  background: ${cream};
  color: ${ink};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 600px) { padding: 0 16px; }
`;

// ---------- Hero ----------
const Hero = styled.section`
  position: relative;
  min-height: 92vh;
  background:
    radial-gradient(ellipse 60% 50% at 80% 0%, rgba(251, 191, 36, 0.18), transparent 60%),
    radial-gradient(ellipse 50% 40% at 10% 90%, rgba(99, 102, 241, 0.12), transparent 60%),
    linear-gradient(180deg, ${navy} 0%, ${navyMid} 100%);
  color: #fff;
  display: flex;
  align-items: center;
  padding: 80px 0 60px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.4);
  color: ${yellow};
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;

  .dot {
    width: 6px;
    height: 6px;
    background: ${yellow};
    border-radius: 50%;
    box-shadow: 0 0 12px ${yellow};
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 6.5vw, 5rem);
  line-height: 1.02;
  letter-spacing: -2px;
  font-weight: 900;
  margin: 0 0 20px;
  color: #fff;

  .accent {
    display: inline-block;
    position: relative;
    color: ${yellow};
    &::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: 6px;
      height: 14px;
      background: rgba(251, 191, 36, 0.18);
      z-index: -1;
      border-radius: 2px;
    }
  }
`;

const HeroSub = styled.p`
  font-size: 1.15rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.78);
  margin: 0 0 32px;
  max-width: 540px;
`;

const HeroCTAs = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 36px;
`;

const sharedBtn = css`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  &:hover { transform: translateY(-2px); }
`;

const PrimaryBtn = styled(Link)`
  ${sharedBtn};
  background: ${yellow};
  color: ${navy};
  box-shadow: 0 10px 30px rgba(251, 191, 36, 0.35);
  &:hover { background: #fcd34d; box-shadow: 0 14px 36px rgba(251, 191, 36, 0.45); }
`;

const HeroMeta = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  border-top: 1px solid rgba(255,255,255,0.1);
  padding-top: 24px;

  .meta {
    .label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1.5px; color: rgba(255,255,255,0.5); }
    .value { font-size: 1rem; font-weight: 700; margin-top: 4px; color: #fff; }
  }
`;

// ---------- Hero visual ----------
const HeroVisual = styled.div`
  position: relative;
  aspect-ratio: 4/5;
  max-height: 560px;

  @media (max-width: 900px) {
    aspect-ratio: 4/3;
    max-height: 380px;
  }
`;

const VisualPhoto = styled.div<{ src: string; pos: string }>`
  position: absolute;
  ${props => props.pos};
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 60%, rgba(10,31,68,0.5));
  }
`;

const FloatBadge = styled(motion.div)`
  position: absolute;
  background: #fff;
  border-radius: 14px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,0.25);
  z-index: 2;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${yellow};
    color: ${navy};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
  }
  .label { font-size: 0.7rem; color: ${muted}; text-transform: uppercase; letter-spacing: 1px; }
  .value { color: ${ink}; font-weight: 800; font-size: 0.95rem; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const FloatBadgeLeft = styled(FloatBadge)`
  top: 12%;
  left: -20px;
  animation: ${float} 5s ease-in-out infinite;
`;

const FloatBadgeRight = styled(FloatBadge)`
  bottom: 8%;
  right: -10px;
  animation: ${float} 6s ease-in-out infinite 1s;
`;

// ---------- Generic section ----------
const Section = styled.section<{ dark?: boolean }>`
  padding: 100px 0;
  ${p => p.dark && css`background: ${navy}; color: #fff;`}
  @media (max-width: 700px) { padding: 64px 0; }
`;

const SectionEyebrow = styled.div`
  font-size: 0.78rem;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  font-weight: 700;
  color: ${yellowDark};
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.1;
  letter-spacing: -1px;
  font-weight: 900;
  margin: 0 0 16px;
  max-width: 720px;
`;

const SectionLead = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${muted};
  max-width: 600px;
  margin: 0 0 48px;
`;

// ---------- Quick facts ----------
const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: -60px;
  position: relative;
  z-index: 5;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FactCard = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(10, 31, 68, 0.08);
  border: 1px solid ${line};
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(10, 31, 68, 0.12); }

  .icon {
    width: 44px;
    height: 44px;
    background: ${navy};
    color: ${yellow};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
  }
  .label { font-size: 0.72rem; color: ${muted}; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 700; }
  .value { font-size: 1.05rem; color: ${ink}; font-weight: 800; line-height: 1.3; }
`;

// ---------- Pricing ----------
const PriceWrap = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 32px;
  align-items: stretch;
  background: linear-gradient(135deg, ${navy} 0%, ${navyMid} 100%);
  border-radius: 24px;
  padding: 48px;
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    background: ${yellow};
    border-radius: 50%;
    opacity: 0.08;
    top: -200px;
    right: -100px;
    filter: blur(40px);
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    padding: 32px 24px;
  }
`;

const PriceMain = styled.div`
  position: relative;
  z-index: 1;

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(251,191,36,0.18);
    border: 1px solid rgba(251,191,36,0.4);
    color: ${yellow};
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    margin-bottom: 16px;
    letter-spacing: 0.5px;
  }
  .strike {
    color: rgba(255,255,255,0.5);
    text-decoration: line-through;
    font-size: 1.2rem;
    margin-right: 12px;
  }
  .amount {
    font-size: clamp(3rem, 7vw, 5rem);
    font-weight: 900;
    line-height: 1;
    letter-spacing: -2px;
  }
  .gst {
    color: rgba(255,255,255,0.7);
    font-size: 0.95rem;
    margin-top: 8px;
  }
  .cta {
    margin-top: 28px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: ${yellow};
    color: ${navy};
    padding: 16px 32px;
    border-radius: 12px;
    font-weight: 800;
    text-decoration: none;
    transition: transform 0.2s;
    &:hover { transform: translateY(-2px); }
  }
`;

const PriceFeatures = styled.div`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 18px;
  padding: 28px;
  position: relative;
  z-index: 1;

  h4 { margin: 0 0 18px; font-size: 0.78rem; letter-spacing: 2px; text-transform: uppercase; color: ${yellow}; font-weight: 700; }
  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 0.95rem;
    color: rgba(255,255,255,0.92);
    svg { color: ${yellow}; flex-shrink: 0; }
  }
`;

// ---------- Tracks ----------
const TrackGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const TrackCard = styled(motion.div)`
  background: #fff;
  border: 1px solid ${line};
  border-radius: 20px;
  padding: 36px;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
  cursor: default;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(10,31,68,0.12);
    border-color: ${yellow};
  }

  .icon-wrap {
    width: 64px;
    height: 64px;
    background: ${navy};
    color: ${yellow};
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    margin-bottom: 20px;
  }
  .pill {
    display: inline-block;
    background: ${cream};
    border: 1px solid ${yellow};
    color: ${yellowDark};
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }
  h3 {
    font-size: 1.4rem;
    margin: 0 0 12px;
    color: ${ink};
    font-weight: 800;
    line-height: 1.25;
  }
  p {
    color: ${muted};
    font-size: 0.95rem;
    line-height: 1.6;
    margin: 0 0 20px;
  }
  .skills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    .skill {
      background: ${cream};
      color: ${navy};
      padding: 5px 12px;
      border-radius: 6px;
      font-size: 0.78rem;
      font-weight: 600;
    }
  }
`;

// ---------- Batches ----------
const BatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const BatchCard = styled.div<{ accent?: string }>`
  background: #fff;
  border: 1px solid ${line};
  border-radius: 16px;
  padding: 28px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    border-color: ${p => p.accent || yellow};
    box-shadow: 0 20px 50px rgba(10,31,68,0.1);
  }

  .num {
    position: absolute;
    top: -10px; right: 16px;
    font-size: 5rem;
    font-weight: 900;
    color: ${cream};
    line-height: 1;
    z-index: 0;
    user-select: none;
  }
  .icon {
    width: 50px;
    height: 50px;
    background: ${p => p.accent || yellow};
    color: ${navy};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    margin-bottom: 16px;
    position: relative;
    z-index: 1;
  }
  .label { font-size: 0.72rem; letter-spacing: 1.5px; text-transform: uppercase; color: ${muted}; font-weight: 700; position: relative; z-index: 1; }
  .tier { font-size: 1.5rem; font-weight: 900; color: ${ink}; margin: 4px 0; position: relative; z-index: 1; }
  .classes { font-size: 0.95rem; color: ${muted}; position: relative; z-index: 1; }
`;

// ---------- Activities ----------
const ActivitiesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
`;

const ActChip = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 24px 16px;
  text-align: center;
  transition: all 0.3s;

  &:hover {
    background: rgba(251,191,36,0.08);
    border-color: rgba(251,191,36,0.3);
    transform: translateY(-3px);
  }

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${yellow};
    color: ${navy};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
    font-size: 1.3rem;
  }
  .name { font-weight: 700; font-size: 0.9rem; color: #fff; line-height: 1.3; }
`;

// ---------- Final CTA ----------
const FinalCTA = styled.div`
  background:
    radial-gradient(ellipse at top right, rgba(251,191,36,0.2), transparent 50%),
    ${navy};
  color: #fff;
  border-radius: 28px;
  padding: 72px 48px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h2 {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    margin: 0 0 16px;
    font-weight: 900;
    letter-spacing: -1px;
    color: #fff;
  }
  p { color: rgba(255,255,255,0.75); font-size: 1.1rem; margin: 0 0 32px; max-width: 560px; margin-left: auto; margin-right: auto; }

  @media (max-width: 700px) { padding: 48px 24px; }
`;

const Venue = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  padding: 12px 20px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 999px;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.85);
  svg { color: ${yellow}; }
`;

// ---------- Sticky mobile CTA ----------
const MobileBar = styled.div`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid ${line};
  padding: 12px 16px;
  z-index: 100;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -8px 24px rgba(0,0,0,0.08);

  .price { color: ${ink}; font-weight: 800; font-size: 1.1rem; }
  .price small { color: ${muted}; font-weight: 500; font-size: 0.75rem; display: block; }
  a {
    background: ${navy};
    color: #fff;
    padding: 12px 22px;
    border-radius: 10px;
    font-weight: 700;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: 700px) { display: flex; }
`;

const Spacer = styled.div`
  display: none;
  height: 70px;
  @media (max-width: 700px) { display: block; }
`;

const PartnerBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 32px;
  opacity: 0.85;

  img { height: 36px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.9; }
  .label { font-size: 0.7rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 2px; }
`;

// =================== Component ===================

const SummerCamp: React.FC = () => {
  const price = 5000;

  return (
    <Page>
      <Hero>
        <Container>
          <HeroGrid>
            <div>
              <PartnerBar>
                <span className="label">In Partnership With</span>
                <img src="/images/iiit-delhi.png" alt="IIIT Delhi" />
                <img src="/images/logo.png" alt="Dream Advance" />
              </PartnerBar>

              <Eyebrow><span className="dot" /> IIITD-IC Summer Camp 2026 · 10 Days</Eyebrow>

              <HeroTitle>
                Where young<br />
                innovators <span className="accent">build</span><br />
                the future.
              </HeroTitle>

              <HeroSub>
                A 10-day, hands-on tech camp at IIIT Delhi. Robotics, IoT, AI, drones, and aero-modelling — taught by people who build the real thing. Take your kit home.
              </HeroSub>

              <HeroCTAs>
                <PrimaryBtn to="/register">
                  Register Now <FaArrowRight />
                </PrimaryBtn>
              </HeroCTAs>

              <HeroMeta>
                <div className="meta">
                  <div className="label">Dates</div>
                  <div className="value">21 – 31 May 2026</div>
                </div>
                <div className="meta">
                  <div className="label">Time</div>
                  <div className="value">9 AM – 12 PM</div>
                </div>
                <div className="meta">
                  <div className="label">Venue</div>
                  <div className="value">IIIT Delhi</div>
                </div>
              </HeroMeta>
            </div>

            <HeroVisual>
              <VisualPhoto
                src="/images/gallery/Robotics.jpg"
                pos="top: 0; right: 0; width: 80%; height: 75%;"
              />
              <VisualPhoto
                src="/images/gallery/1.jpeg"
                pos="bottom: 0; left: 0; width: 60%; height: 50%;"
                style={{ border: `4px solid ${cream}` }}
              />
              <FloatBadgeLeft>
                <div className="icon"><FaCertificate /></div>
                <div>
                  <div className="label">Outcome</div>
                  <div className="value">Take-home kit</div>
                </div>
              </FloatBadgeLeft>
              <FloatBadgeRight>
                <div className="icon"><FaUsers /></div>
                <div>
                  <div className="label">Class</div>
                  <div className="value">Grades 4–12</div>
                </div>
              </FloatBadgeRight>
            </HeroVisual>
          </HeroGrid>
        </Container>
      </Hero>

      {/* Quick facts overlapping hero */}
      <Container>
        <FactsGrid>
          <FactCard>
            <div className="icon"><FaCalendarAlt /></div>
            <div className="label">Dates</div>
            <div className="value">21 – 31 May 2026</div>
          </FactCard>
          <FactCard>
            <div className="icon"><FaClock /></div>
            <div className="label">Daily</div>
            <div className="value">9 AM – 12 PM</div>
          </FactCard>
          <FactCard>
            <div className="icon"><FaTools /></div>
            <div className="label">Tracks</div>
            <div className="value">Robotics · Drones</div>
          </FactCard>
          <FactCard>
            <div className="icon"><FaUsers /></div>
            <div className="label">For</div>
            <div className="value">Classes 4 – 12</div>
          </FactCard>
        </FactsGrid>
      </Container>

      {/* Pricing */}
      <Section>
        <Container>
          <Reveal>
            <SectionEyebrow>Pricing</SectionEyebrow>
            <SectionTitle>One price. Kit included. No surprises.</SectionTitle>
            <SectionLead>
              Materials, take-home kit, certificate, and a final-day showcase — all in.
              Limited seats available — register now.
            </SectionLead>
          </Reveal>

          <Reveal delay={0.1}>
            <PriceWrap>
              <PriceMain>
                <span className="badge">
                  <FaBolt /> LIMITED OFFER · Save ₹{(11999 - price).toLocaleString('en-IN')}
                </span>
                <div>
                  <span className="strike">₹11,999</span>
                  <span className="amount">₹{price.toLocaleString('en-IN')}</span>
                </div>
                <div className="gst">+ 18% GST</div>
                <Link to="/register" className="cta">
                  Reserve a Seat <FaArrowRight />
                </Link>
              </PriceMain>

              <PriceFeatures>
                <h4>What's included</h4>
                <ul>
                  <li><FaCheckCircle /> Take-home Arduino / drone kit</li>
                  <li><FaCheckCircle /> All tools & materials</li>
                  <li><FaCheckCircle /> Certificate of completion</li>
                  <li><FaCheckCircle /> Final-day showcase event</li>
                  <li><FaCheckCircle /> Mentorship from IIIT Delhi</li>
                </ul>
              </PriceFeatures>
            </PriceWrap>
          </Reveal>
        </Container>
      </Section>

      {/* Tracks */}
      <Section style={{ background: '#fff', borderTop: `1px solid ${line}`, borderBottom: `1px solid ${line}` }}>
        <Container>
          <Reveal>
            <SectionEyebrow>Choose your track</SectionEyebrow>
            <SectionTitle>Two tracks. Both hands-on.</SectionTitle>
            <SectionLead>
              Pick what excites your child. Each track is an end-to-end build experience —
              from concept to a working artifact you take home.
            </SectionLead>
          </Reveal>

          <TrackGrid>
            <Reveal delay={0.05}>
              <TrackCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaRobot /></div>
                <span className="pill">TRACK 1</span>
                <h3>Robotics + IoT + Artificial Intelligence</h3>
                <p>
                  Build a sensor-driven robot, wire it to the cloud, and add a layer of AI to make it think.
                  Arduino, Python basics, and real-world IoT projects.
                </p>
                <div className="skills">
                  <span className="skill">Arduino</span>
                  <span className="skill">Sensors</span>
                  <span className="skill">Python</span>
                  <span className="skill">Cloud IoT</span>
                  <span className="skill">ML basics</span>
                </div>
              </TrackCard>
            </Reveal>
            <Reveal delay={0.15}>
              <TrackCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaPlane /></div>
                <span className="pill">TRACK 2</span>
                <h3>Drone Tech + Aero Modelling</h3>
                <p>
                  Assemble, calibrate, and fly your own quadcopter. Learn the physics of flight, aerodynamics,
                  and finish with a drone-race showcase.
                </p>
                <div className="skills">
                  <span className="skill">Quadcopter</span>
                  <span className="skill">Flight controllers</span>
                  <span className="skill">Aerodynamics</span>
                  <span className="skill">Drone race</span>
                </div>
              </TrackCard>
            </Reveal>
          </TrackGrid>
        </Container>
      </Section>

      {/* Batches */}
      <Section>
        <Container>
          <Reveal>
            <SectionEyebrow>Three batches</SectionEyebrow>
            <SectionTitle>Right pace, right peers.</SectionTitle>
            <SectionLead>
              Students are grouped by class so the curriculum stays age-appropriate
              and discussions stay engaged.
            </SectionLead>
          </Reveal>

          <BatchGrid>
            <Reveal delay={0.05}>
              <BatchCard>
                <span className="num">1</span>
                <div className="icon"><FaChild /></div>
                <div className="label">Batch 01 · Junior</div>
                <div className="tier">Spark</div>
                <div className="classes">For students in Classes 4 – 6</div>
              </BatchCard>
            </Reveal>
            <Reveal delay={0.12}>
              <BatchCard accent="#f59e0b">
                <span className="num">2</span>
                <div className="icon"><FaUserGraduate /></div>
                <div className="label">Batch 02 · Middle</div>
                <div className="tier">Build</div>
                <div className="classes">For students in Classes 7 – 9</div>
              </BatchCard>
            </Reveal>
            <Reveal delay={0.19}>
              <BatchCard accent="#0a1f44">
                <span className="num">3</span>
                <div className="icon"><FaUserTie /></div>
                <div className="label">Batch 03 · Senior</div>
                <div className="tier">Launch</div>
                <div className="classes">For students in Classes 10 – 12</div>
              </BatchCard>
            </Reveal>
          </BatchGrid>
        </Container>
      </Section>

      {/* Activities — dark band */}
      <Section dark>
        <Container>
          <Reveal>
            <SectionEyebrow style={{ color: yellow }}>Beyond the build</SectionEyebrow>
            <SectionTitle style={{ color: '#fff' }}>It's not all soldering.</SectionTitle>
            <SectionLead style={{ color: 'rgba(255,255,255,0.7)' }}>
              Every day mixes deep technical work with brain-breaks designed to keep curiosity high.
            </SectionLead>
          </Reveal>

          <ActivitiesRow>
            <Reveal delay={0.05}><ActChip><div className="icon"><FaGamepad /></div><div className="name">Fun Games</div></ActChip></Reveal>
            <Reveal delay={0.1}><ActChip><div className="icon"><FaBookOpen /></div><div className="name">Vedic Maths</div></ActChip></Reveal>
            <Reveal delay={0.15}><ActChip><div className="icon"><FaTrophy /></div><div className="name">Competitions</div></ActChip></Reveal>
            <Reveal delay={0.2}><ActChip><div className="icon"><FaBrain /></div><div className="name">Memory Boosters</div></ActChip></Reveal>
            <Reveal delay={0.25}><ActChip><div className="icon"><FaMusic /></div><div className="name">Music & Dance</div></ActChip></Reveal>
          </ActivitiesRow>
        </Container>
      </Section>

      {/* Final CTA */}
      <Section>
        <Container>
          <Reveal>
            <FinalCTA>
              <SectionEyebrow style={{ color: yellow, justifyContent: 'center' }}>Limited Seats</SectionEyebrow>
              <h2>Ready to give them a head start?</h2>
              <p>Reserve a seat at IIIT Delhi this May. Limited seats — kit included.</p>
              <PrimaryBtn to="/register">
                Register Now <FaArrowRight />
              </PrimaryBtn>
              <Venue>
                <FaMapMarkerAlt />
                IIIT Delhi Campus, New Delhi 110020
              </Venue>
              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                <span><FaPhone style={{ marginRight: 8, color: yellow }} />+91 73806 12016</span>
              </div>
            </FinalCTA>
          </Reveal>
        </Container>
      </Section>

      <Spacer />
      <MobileBar>
        <div className="price">
          <span style={{ textDecoration: 'line-through', color: muted, fontWeight: 500, fontSize: '0.85rem', marginRight: 6 }}>₹11,999</span>
          ₹{price.toLocaleString('en-IN')} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: muted }}>+ GST</span>
          <small>Save ₹{(11999 - price).toLocaleString('en-IN')}</small>
        </div>
        <Link to="/register">
          Register <FaArrowRight />
        </Link>
      </MobileBar>
    </Page>
  );
};

export default SummerCamp;
